import localforage from 'localforage';
import { EventBus } from 'native-pubsub';

export interface SameWindowStorageChangeEvent {
  key: string;
  oldValue: string | null;
  newValue: string | null;
  storageArea: Storage;
}

interface Stringifyable {
  toString: () => string;
}

export const sameWindowStorageChangeEventBus = new EventBus<SameWindowStorageChangeEvent>();

export const getItemFromStorage = (key: string, storage: Storage = localStorage) => {
  return storage.getItem(key);
};

export const saveItemToStorage = (key: string, value: string, storage: Storage = localStorage) => {
  const oldValue = storage.getItem(key);
  const result = storage.setItem(key, value);

  sameWindowStorageChangeEventBus.publish({ key, oldValue, newValue: value, storageArea: storage });

  return result;
};

export const removeItemFromStorage = (key: string, storage: Storage = localStorage) => {
  const oldValue = storage.getItem(key);
  const result = storage.removeItem(key);

  sameWindowStorageChangeEventBus.publish({ key, oldValue, newValue: null, storageArea: storage });

  return result;
};

export const getItemFromDB = async (key: string) => {
  return localforage.getItem(key) as Promise<string | null>;
};

export const saveItemToDB = async (key: Stringifyable, value: unknown) => {
  return localforage.setItem(key.toString(), value);
};

export const removeItemFromDB = async (key: Stringifyable, storageInstance?: LocalForage) => {
  const storage = storageInstance || localforage;
  return storage.removeItem(key.toString());
};

interface DbCacheItem<TValue = unknown> {
  value: TValue;
  timestamp: number;
  maxAge: number;
}

export const saveCacheItemToStorage = (
  key: Stringifyable,
  value: unknown,
  /** in milliseconds */
  maxAge: number,
  storage: Storage = localStorage,
) => {
  const keyString = key.toString();
  return saveItemToStorage(
    keyString,
    JSON.stringify({
      value,
      timestamp: Date.now(),
      maxAge,
    } satisfies DbCacheItem),
    storage,
  );
};

export const saveCacheItemToDb = async (
  key: Stringifyable,
  value: unknown,
  /** in milliseconds */
  maxAge: number,
  storageInstance?: LocalForage,
) => {
  const storage = storageInstance || localforage;
  const keyString = key.toString();
  return storage.setItem(keyString, {
    value,
    timestamp: Date.now(),
    maxAge,
  } satisfies DbCacheItem);
};

export const getCacheItemFromDb = async <TData>(key: Stringifyable, storageInstance?: LocalForage) => {
  const keyString = key.toString();
  const storage = storageInstance || localforage;
  const item = await storage.getItem<DbCacheItem<TData>>(keyString);
  if (item) {
    if (Date.now() - item.timestamp < item.maxAge) {
      return item.value;
    } else {
      await storage.removeItem(keyString);
    }
  }
};
